import type { ReactElement, ReactNode } from 'react';

/* A bounded region with an optional header. The unit every screen composes from.
 *
 * `flush` exists for the one case that needs it: a card whose only child is a table. The table draws
 * its own row separators, so the usual body padding leaves a visible gap between the header rule and
 * the first row — and a screen working around that with a negative margin is how a design system
 * starts being fought rather than used.
 */

export interface CardProps {
  /* `| undefined` on every optional prop. `exactOptionalPropertyTypes` is on, so a caller
   * forwarding a conditional — `count={ready ? label : undefined}` — cannot pass explicit
   * undefined to a bare `prop?: T`, and every screen does exactly that. */
  readonly title?: string | undefined;
  /** Right-aligned in the header. A count, a timestamp — context, not a control.
   *
   * **`ReactNode` rather than `string`, widened for the scheduling cards.** Those carry a state
   * chip on the title baseline — Enabled / Disabled / Suspended until … — and the alternative was a
   * hand-built header row inside the body, which would put the chip below the rule the header draws
   * and leave one card in the detail grid with a different anatomy from its two neighbours.
   *
   * Still context and still not a control: a node here is a badge or a formatted figure, never a
   * button. Every existing caller passes a string and is unaffected — `ReactNode` includes it.
   */
  readonly hint?: ReactNode;
  readonly flush?: boolean;
  readonly children: ReactNode;
}

export function Card({ title, hint, flush = false, children }: CardProps): ReactElement {
  return (
    <section className="lq-card">
      {title !== undefined && (
        <div className="lq-card__header">
          {/* h2, because `PageHeader` owns the h1 on every screen. A card that chose its own level
              would produce a document outline that jumps from h1 to h3. */}
          <h2 className="lq-card__title">{title}</h2>
          {hint !== undefined && <span className="lq-card__hint">{hint}</span>}
        </div>
      )}
      <div className={flush ? 'lq-card__body lq-card__body--flush' : 'lq-card__body'}>
        {children}
      </div>
    </section>
  );
}
